import { useState, useEffect } from "react";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LogOut, AlertTriangle, Lock, Globe, CheckCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FOX_IMG = "https://static.prod-images.emergentagent.com/jobs/75789c9b-0d29-4ec0-9a3b-e1fc05fe9785/images/5b2ab7fd29746777696641592f7d349c0b2930b2e79250b8a28c9a3f926de55d.png";

export function MembershipLock({ user, token, lang: initialLang, onLogout, onPaymentSuccess }) {
  const [lang, setLang] = useState(initialLang || "en");
  const [paypalConfig, setPaypalConfig] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [paymentDone, setPaymentDone] = useState(false);
  const [selectedTier, setSelectedTier] = useState("basic"); // basic | advanced
  const isAr = lang === "ar";

  useEffect(() => {
    axios.get(`${API}/paypal/config`).then((r) => setPaypalConfig(r.data)).catch(() => {});
  }, []);

  const T = isAr
    ? {
        title: "حسابك غير مفعّل",
        hello: `مرحباً ${user?.name || ""}`,
        body: "اشتراكك منتهي أو غير مفعّل بعد. لتفعيل حسابك، الرجاء الدفع $10 عبر PayPal لمدة شهر كامل.",
        termsTitle: "شروط الاستخدام",
        term1: "هذا الجهاز هو الجهاز الوحيد المسموح لك بالدخول منه. لاستخدام جهاز آخر، يلزم حساب جديد واشتراك مدفوع منفصل.",
        term2: "يحق لك إجراء تحليل واحد فقط كل 3 دقائق على الماركت والفريم الزمني من اختيارك.",
        term3: "أي محاولة لمشاركة الحساب أو تسجيل دخول من جهاز آخر سيتم رفضها تلقائياً وتنبيه الإدارة.",
        logout: "تسجيل خروج",
        switchLang: "English",
        processing: "جاري معالجة الدفع...",
        success: "تم الدفع بنجاح! يتم تفعيل حسابك...",
        error: "حدث خطأ في الدفع. حاول مرة أخرى.",
        chooseTier: "اختر باقتك",
        basic: "عادي",
        basicDesc: "إشارة الذهب من Combination فقط",
        advanced: "متقدم",
        advancedDesc: "كل الأسواق + كل التقنيات والاستراتيجيات",
      }
    : {
        title: "Your account is not active",
        hello: `Hello ${user?.name || ""}`,
        body: "Your subscription is expired or not yet activated. Choose your plan and pay via PayPal.",
        termsTitle: "Usage Terms",
        term1: "This device is the only device allowed to access your account. To use another device, a new account and a separate paid subscription are required.",
        term2: "You are entitled to only one analysis every 3 minutes on the market and timeframe of your choice.",
        term3: "Any attempt to share the account or sign in from another device will be automatically rejected and reported to admin.",
        logout: "Logout",
        switchLang: "العربية",
        processing: "Processing payment...",
        success: "Payment successful! Activating your account...",
        error: "Payment error. Please try again.",
        chooseTier: "Choose your plan",
        basic: "Basic",
        basicDesc: "Gold Combination signal only",
        advanced: "Advanced",
        advancedDesc: "All markets + all techniques & strategies",
      };

  const createOrder = async () => {
    setPaymentError("");
    const res = await axios.post(
      `${API}/paypal/create-order?tier=${selectedTier}`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return res.data.order_id;
  };

  const onApprove = async (data) => {
    setProcessing(true);
    try {
      await axios.post(
        `${API}/paypal/capture-order/${data.orderID}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPaymentDone(true);
      setTimeout(() => {
        if (onPaymentSuccess) onPaymentSuccess();
        else window.location.reload();
      }, 1500);
    } catch (e) {
      setPaymentError(e.response?.data?.detail || T.error);
      setProcessing(false);
    }
  };

  return (
    <div
      dir={isAr ? "rtl" : "ltr"}
      className="min-h-screen flex items-center justify-center p-3 sm:p-4 bg-gradient-to-br from-pink-100 via-purple-100 to-cyan-100"
    >
      <Card className="max-w-lg w-full bg-white/95 backdrop-blur border-4 border-pink-300 shadow-2xl shadow-pink-300/40 rounded-3xl">
        <CardContent className="p-5 sm:p-8" data-testid="membership-lock">
          {/* Language toggle */}
          <div className="flex justify-end mb-2">
            <button
              onClick={() => setLang(isAr ? "en" : "ar")}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-pink-100 hover:bg-pink-200 text-pink-700 border border-pink-300 font-semibold"
              data-testid="lock-lang-toggle"
            >
              <Globe className="w-3.5 h-3.5" />
              {T.switchLang}
            </button>
          </div>

          {/* Mascot + Title */}
          <div className="text-center">
            <img src={FOX_IMG} alt="" className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3 rounded-full border-4 border-pink-300 shadow-lg" />
            <div className="flex items-center justify-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-500" />
              <h1 className="text-xl sm:text-2xl font-extrabold text-pink-600">{T.title}</h1>
            </div>
            <p className="text-zinc-700 text-sm mb-1">{T.hello}</p>
            <p className="text-zinc-600 text-xs sm:text-sm mb-4 leading-relaxed">{T.body}</p>
          </div>

          {/* Usage Terms */}
          <div className="bg-pink-50 border-2 border-pink-200 rounded-2xl p-3 sm:p-4 mb-4 text-xs">
            <p className="flex items-center gap-1.5 font-bold text-pink-700 mb-2">
              <Lock className="w-4 h-4" />
              {T.termsTitle}
            </p>
            <ul className="space-y-1.5 text-zinc-700 list-disc list-inside leading-relaxed">
              <li>{T.term1}</li>
              <li>{T.term2}</li>
              <li>{T.term3}</li>
            </ul>
          </div>

          {/* Payment status banners */}
          {paymentDone && (
            <div className="bg-green-100 border-2 border-green-300 text-green-700 p-3 rounded-2xl mb-3 flex items-center gap-2 text-sm" data-testid="payment-success">
              <CheckCircle className="w-5 h-5" />
              <span>{T.success}</span>
            </div>
          )}
          {processing && !paymentDone && (
            <div className="bg-blue-100 border-2 border-blue-300 text-blue-700 p-3 rounded-2xl mb-3 flex items-center gap-2 text-sm">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>{T.processing}</span>
            </div>
          )}
          {paymentError && (
            <div className="bg-red-100 border-2 border-red-300 text-red-700 p-3 rounded-2xl mb-3 text-sm" data-testid="payment-error">
              {paymentError}
            </div>
          )}

          {/* Tier selector */}
          {!paymentDone && (
            <div className="mb-4">
              <p className="text-xs font-bold text-zinc-700 mb-2">{T.chooseTier}</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setSelectedTier("basic")}
                  className={`text-${isAr ? "right" : "left"} rounded-2xl border-2 p-3 transition-all ${
                    selectedTier === "basic"
                      ? "bg-pink-50 border-pink-400 ring-2 ring-pink-300"
                      : "bg-white border-zinc-200 hover:border-pink-200"
                  }`}
                  data-testid="tier-basic-btn"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-pink-600">{T.basic}</span>
                    <span className="font-mono font-bold text-zinc-800">$20</span>
                  </div>
                  <p className="text-[10px] text-zinc-500 mt-1 leading-tight">{T.basicDesc}</p>
                </button>
                <button
                  onClick={() => setSelectedTier("advanced")}
                  className={`text-${isAr ? "right" : "left"} rounded-2xl border-2 p-3 transition-all ${
                    selectedTier === "advanced"
                      ? "bg-purple-50 border-purple-400 ring-2 ring-purple-300"
                      : "bg-white border-zinc-200 hover:border-purple-200"
                  }`}
                  data-testid="tier-advanced-btn"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-purple-600">{T.advanced}</span>
                    <span className="font-mono font-bold text-zinc-800">$40</span>
                  </div>
                  <p className="text-[10px] text-zinc-500 mt-1 leading-tight">{T.advancedDesc}</p>
                </button>
              </div>
            </div>
          )}

          {/* PayPal Smart Buttons */}
          {paypalConfig?.client_id && !paymentDone && (
            <div className="mb-3" data-testid="paypal-buttons-container">
              <PayPalScriptProvider
                options={{
                  clientId: paypalConfig.client_id,
                  currency: "USD",
                  intent: "capture",
                }}
              >
                <PayPalButtons
                  key={selectedTier}
                  style={{ layout: "vertical", color: "blue", shape: "pill", label: "pay", height: 48 }}
                  disabled={processing}
                  createOrder={createOrder}
                  onApprove={onApprove}
                  onError={(err) => {
                    console.error("PayPal error", err);
                    setPaymentError(T.error);
                  }}
                />
              </PayPalScriptProvider>
            </div>
          )}

          <Button
            variant="outline"
            onClick={onLogout}
            className="w-full border-2 border-pink-200 text-pink-600 hover:bg-pink-50 rounded-full"
            data-testid="logout-from-lock"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {T.logout}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
